import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import { FaStar, FaRegHeart, FaMapMarkerAlt } from 'react-icons/fa';
import { API_BASE_URL } from '../../config';
import styles from './SponsorsPage.module.css';

// Components
import Header from '../../components/Common/Header'; 
import Footer from '../../components/Common/Footer';
import SponsorDrawer from '../../components/FeaturedBusinessBanner/SponsorDrawer'; // Adjust path if needed

// --- HELPER FUNCTIONS ---
function toAbsoluteImageUrl(url) {
  if (!url || typeof url !== "string") return "https://via.placeholder.com/400x300?text=No+Image";
  const trimmed = url.trim();
  if (trimmed.startsWith("data:") || trimmed.startsWith("blob:")) return trimmed;
  const maybeBase64 = /^[A-Za-z0-9+/=]+$/.test(trimmed) && trimmed.length > 100 && !/^https?:\/\//i.test(trimmed);
  if (maybeBase64) return `data:image/jpeg;base64,${trimmed}`;
  try {
    const test = new URL(trimmed);
    return test.href;
  } catch (e) {}
  if (trimmed.startsWith("/")) return `${API_BASE_URL}${trimmed}`;
  return trimmed;
}

const normalizeBusiness = (b) => {
  const id = b.businessId || b.businessUserId || b.id;
  const name = b.businessName || b.name || "Business";
  const suburb = b.suburb || "";
  const postcode = b.postCode || b.postcode || "";
  const rawImage = b.profileImage || b.profilePic || b.image || null;
  const logo = toAbsoluteImageUrl(rawImage);
  const rawCover = b.CoverImage || b.coverImage || null;
  const coverImage = rawCover ? toAbsoluteImageUrl(rawCover) : null;
  const rating = b.overallRating !== null && b.overallRating !== undefined ? parseFloat(b.overallRating) : 0;
  const type = suburb ? `Top Rated in ${suburb}` : "Verified Partner";

  return {
    id,
    name,
    type,
    image: coverImage || logo,
    logo,
    coverImage,
    rating,
    reviewCount: b.reviewCount || 0,
    suburb,
    postcode,
    description: b.description || b.profileDescription || "No description available.",
    email: b.businessEmail,
    phone: b.businessPhone,
    website: b.website,
    location: suburb && postcode ? `${suburb}, ${postcode}` : suburb
  };
};

const SponsorsPage = () => {
  const navigate = useNavigate();
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- DRAWER STATE ---
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  useEffect(() => {
    const fetchSponsors = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/Business/getFeaturedBusinesses`);
        const list = Array.isArray(response.data) ? response.data : [];
        const normalized = list.map(normalizeBusiness).filter((x) => x.id !== null);
        setSponsors(normalized);
      } catch (error) {
        console.error("Failed to fetch sponsors", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSponsors();
  }, []);

  // --- HANDLERS ---
  
  const openDrawer = (e, business) => {
    e.preventDefault(); 
    setSelectedBusiness(business);
    setIsDrawerOpen(true);
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setTimeout(() => setSelectedBusiness(null), 300);
  };

  const handleDeepLinkSave = (e, businessId) => {
    e.preventDefault();
    e.stopPropagation(); // Stop the card click event (drawer)
    // Navigate to the handler route
    navigate(`/save-sponsor/${businessId}`);
  };

  return (
    <>
    <Header />
    <div className={styles.pageWrapper}>
      <div className={styles.container}>
        
        <header className={styles.header}>
            <h1 className={styles.title}>
                Our <span className={styles.highlightGold}>Premier Partners</span>
            </h1>
            <p className={styles.subtitle}>
                Explore our most trusted construction team professionals. These partners have demonstrated excellence, reliability, and quality craftsmanship.
            </p>
        </header>

        {loading ? (
           <div style={{textAlign: 'center', padding: '50px', color: '#999'}}>Loading Partners...</div>
        ) : (
            <main className={styles.grid}>
                {sponsors.map((sponsor) => (
                    // 1. Update onClick to open Drawer
                    <a 
                        href={`/business/${sponsor.id}`} 
                        key={sponsor.id} 
                        className={styles.card}
                        onClick={(e) => openDrawer(e, sponsor)}
                    >
                        <div className={styles.imageWrapper}>
                            <img 
                                src={sponsor.image} 
                                alt={sponsor.name} 
                                className={styles.image} 
                                loading="lazy" 
                            />
                            {/* 2. Update Save Button to use Deep Link */}
                            <button 
                                className={styles.saveBtn} 
                                onClick={(e) => handleDeepLinkSave(e, sponsor.id)}
                                title="Save to Favourites"
                            >
                                <FaRegHeart />
                            </button>
                        </div>
                        
                        <div className={styles.content}>
                            <h3 className={styles.businessName}>{sponsor.name}</h3>
                            <span className={styles.businessType}>{sponsor.type}</span>
                            
                            <div className={styles.ratingRow}>
                                <div className={styles.stars}>
                                    <FaStar color="#ffd700" />
                                </div>
                                <span style={{fontWeight:'bold', marginLeft:'5px'}}>
                                    {sponsor.rating > 0 ? sponsor.rating.toFixed(1) : "New"}
                                </span>
                                
                                {sponsor.rating > 0 && (
                                    <span className={styles.reviewCount}>
                                        ({sponsor.reviewCount > 0 ? sponsor.reviewCount : "Verified"})
                                    </span>
                                )}
                            </div>
                            
                            {sponsor.suburb && (
                                <div style={{fontSize: '12px', color: '#888', marginTop: '8px', display:'flex', alignItems:'center', gap:'4px'}}>
                                    <FaMapMarkerAlt size={10} /> {sponsor.suburb}, {sponsor.postcode}
                                </div>
                            )}
                        </div>
                    </a>
                ))}
            </main>
        )}

      </div>
    </div>
    <Footer />

    {/* 3. Render the Drawer */}
    <SponsorDrawer 
        business={selectedBusiness} 
        isOpen={isDrawerOpen} 
        onClose={closeDrawer} 
    />
    </>
  );
};

export default SponsorsPage;
